import _ from 'lodash';

import React, { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Tooltip, Typography, useMediaQuery } from "@material-ui/core";
import { styled, useTheme } from '@material-ui/core/styles';
import EventBusyIcon from '@material-ui/icons/EventBusyRounded';
import DoneIcon from '@material-ui/icons/Done';
import TrendingUpIcon from '@material-ui/icons/TrendingUpRounded';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import TabletMacIcon from '@material-ui/icons/TabletMac';
import DesktopMacIcon from '@material-ui/icons/DesktopMac';
import SpeedIcon from '@material-ui/icons/Speed';

import RecordScreenshot from './landing/RecordScreenshot.png';
import InviteScreenshot from './landing/InviteScreenshot.png';
import ReviewScreenshot from './landing/ReviewScreenshot.png';
import HeroPoster from './landing/HeroPoster.png';
import HeroVideo from './landing/HeroVideo.mp4';

import { HeroHeading, SecondaryHeading, TertiaryHeading } from '../../components/Headings'
import TryItNowArea from '../TryItNowArea';
import useGoogleTagManager from '../../hooks/useGoogleTagManager';
import Rating from '@material-ui/lab/Rating';

import ChickFilALogo from './landing/logos/chick-fil-a.svg';
import MetroLagoonsLogo from './landing/logos/metro-lagoons.svg';
import OptimizedPaymentsLogo from './landing/logos/optimized-payments.svg';
import StriveHealthLogo from './landing/logos/strive-health.svg';
import TaipeiAmericanSchoolLogo from './landing/logos/taipei-american-school.svg';

const ReviewItem = ({ personName, personRole, paragraphs }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const paragraphElements = _.flatMap(paragraphs, (paragraph, index) => {
    const isFirst = index <= 0;
    const quoteCharElement = isFirst
      ? <span style={{ position: "absolute", top: -20, left: -25, fontSize: "2rem", color: theme.palette.primary.main }}>❝</span>
      : null;
    const isNotLast = index < paragraphs.length - 1;
    const breakElement = isNotLast ? <br /> : null;
    return [
      <Typography variant="h6" style={{ fontWeight: "normal" }} gutterBottom>
        {quoteCharElement}
        {paragraph}
      </Typography>,
      breakElement,
    ];
  });

  return (
    <Box position="relative" mb={6} ml={4} mr={2} fontStyle="italic" textAlign={isSmall ? "left" : "justify"}>
      {paragraphElements}
      <Box mt={2} style={{ display: "flex", alignItems: "center", gap: 15, justifyContent: "space-between" }}>
        <Rating value={5} size="large" readOnly />
        <Box style={{ textAlign: "right" }}>
          <Box style={{ fontStyle: "normal", fontWeight: 500, fontSize: "1.3em" }}>{personName}</Box>
          {personRole && <Box style={{ fontStyle: "normal", fontSize: "1em" }}>{personRole}</Box>}
        </Box>
      </Box>
    </Box>
  );
};

const ReviewsList = () => {
  const reviews = [
    {
      personName: "Kimmy G.",
      personRole: "Recruiter",
      paragraphs: [
        "Canvass turns a 30 minute traditional phone interview with a recruiter into a 10 minute process. It also gives us more valuable insight into if candidates are able to navigate technical prompts like logging in and recording a video. We also require candidates to have a secure and quiet place to work due to HIPAA and this gave us insight into that as well. Anytime we had issues the customer support team was able to quality resolve the issues we were having."
      ],
    },
    {
      personName: "Verified User in Insurance",
      paragraphs: [
        "From the beginning, Canvass has been an intuitive easy to use tool for our recruiting team. There is no over cumbersome setup, large system fees, or complicated user interface. We were set up and using the tool within 24 hours. They’ve built a product that solves a very specific need in the recruiting process without forcing additional products or steps into your process. It integrates perfectly with our greenhouse recruiting system and seamlessly became part of our process. The support team is very responsive and solution oriented. There is a tremendous amount of passion for what they do and they want to ensure every user has an ideal experience. It’s refreshing to work with a company that truly cares about the success of their clients and the experience they have."
      ],
    },
    {
      personName: "Nicole C.",
      personRole: "Early Talent Manager",
      paragraphs: [
        "Everything is incredibly straightforward with Canvass. I've used video interviewing tools for years and expected to receive hundreds of emails from candidates with technical issues around the application window deadline, but was so surprised to see none and all of our candidates' completed interviews! The set up is incredibly easy, Levi kindly took the time to talk me through the platform but it's so simple that you could easily figure it out on your own. The customisable email options are great and it's been wonderful to be able to send automated reminder emails easily."
      ]
    },
    {
      personName: "Samantha D.",
      personRole: "Director of Operations",
      paragraphs: [
        "Canvass is super-lightweight and easy to use. If you can use Google Docs, you can use Canvass. It's also a fraction of the price of other systems like this. We started off thinking credits would be the way to go but ended up changing over to unlimited... then we were able to cancel after just a couple of months, no long-term commitment.",
        "Async video interviews have helped us scale by focusing our attention only on candidates who are actively engaged with our hiring process, rather than chasing after people who aren't interested.",
      ],
    },
    {
      personName: "Verified User in Consulting",
      paragraphs: [
        "The Canvass team is extremely responsive to feedback and often implements relevant use cases quickly. The platform itself is simple to use, reasonably priced, and promotes efficient communication and scheduling across recruiters, candidates, and hiring managers."
      ],
    },
    {
      personName: "Melissa S.",
      personRole: "Human Resources Director",
      paragraphs: [
        "Canvass is great for a budget conscious organization that also has high demanding needs during peak months. We adapted it this year and it's been so helpful in cutting down unnecessary interviews with candidates that were not a match from the start!"
      ],
    },
    {
      personName: "Verified User in Primary/Secondary Education",
      paragraphs: [
        "As a new business with a sizable amount of employees to hire, we were looking for a video component that integrated into our robust ATS. After hoards of investigation and demonstrations, we found Canvass best met our needs. I am a novice ATS administrator and we were looking for a way to video record candidate responses to several prompts. Canvass’ usability, intuitive layout, and outstanding customer service all are worth noting. We have been blown away by their responsiveness to our questions and attentiveness to needs. We highly recommend Canvass, two thumbs raised way up high."
      ]
    }
  ];
  for (let n = 0; n < reviews.length; n += 1) {
    reviews[n].key = n;
  }
  return reviews.map(review => <ReviewItem {...review} />)
};

const ReviewsArea = () => {
  const { t } = useTranslation();
  return (
    <>
      <Box mb={6}>
        <Typography variant="h5" style={{ fontSize: "2.9em", fontWeight: 600 }}>
          {t('Our customers say...')}
        </Typography>
      </Box>
      <ReviewsList />
    </>
  );
};

const StepNumber = styled(({ label, ...props }) => {
  return (
    <div {...props}>
      {label}
    </div>
  );
})(props => ({
  width: '40px',
  height: '40px',
  lineHeight: '40px',
  borderRadius: '50%',
  backgroundColor: props.theme.palette.primary.main,
  color: props.theme.palette.primary.contrastText,
  textAlign: 'center',
  fontSize: '1.5rem'
}));

const HowItWorksStep = ({ stepNumber, image, title, subtitle, ...props }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Box display="flex" flexDirection={isSmall ? 'column' : 'row'} {...props}>
      <Box width={isSmall ? '100%' : '50%'} mb={isSmall ? 2 : 0}>
        <Box minHeight="100%" display="flex" flexDirection="column" justifyContent="center">
          <Box p={isSmall ? 0 : 4}>
            <Box display="flex" alignItems="center">
              <Box mr={1.5}>
                <StepNumber label={stepNumber} />
              </Box>
              <SecondaryHeading color="primary">
                {title}
              </SecondaryHeading>
            </Box>
            <Box mt={2}>
              <TertiaryHeading>
                {subtitle}
              </TertiaryHeading>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        width={isSmall ? '100%' : '50%'}
        display="flex" alignItems="center" justifyItems="center" alignContent="center" justifyContent="center"
      >
        {image}
      </Box>
    </Box>
  );
};

const ReviewStep = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <HowItWorksStep
      stepNumber={3}
      image={<img src={ReviewScreenshot} width={isSmall ? '100%' : '80%'} alt={t('Screenshot of reviewing candidates')} />}
      title={t('Review their responses')}
      subtitle={t('Share candidates with your team and build a shortlist')}
    />
  );
};

const InviteStep = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const phoneIcon = (
    <span>
      <Tooltip title={t('Mobile')} arrow>
        <span><PhoneIphoneIcon /></span>
      </Tooltip>
    </span>
  );
  const tabletIcon = (
    <span>
      <Tooltip title={t('Tablet')} arrow>
        <span><TabletMacIcon /></span>
      </Tooltip>
    </span>
  );
  const desktopIcon = (
    <span>
      <Tooltip title={t('Desktop')} arrow>
        <span><DesktopMacIcon /></span>
      </Tooltip>
    </span>
  );
  return (
    <HowItWorksStep
      stepNumber={2}
      image={<img src={InviteScreenshot} width={isSmall ? '100%' : '80%'} alt={t('Screenshot of inviting candidates')} />}
      title={t('Invite your candidates')}
      subtitle={(
        <>
          <Trans>They record a response in their own time on any device</Trans>&nbsp;({phoneIcon} / {tabletIcon} / {desktopIcon})
        </>
      )}
    />
  );
};

const CreateStep = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <HowItWorksStep
      stepNumber={1}
      image={<img src={RecordScreenshot} width={isSmall ? '100%' : '80%'} alt={t('Screenshot of recording an interview')} />}
      title={t('Create your interview')}
      subtitle={t('Recording the questions in your browser only takes 5 minutes')}
    />
  );
};

const HowItWorksArea = () => {
  return (
    <>
      <Box mb={6}>
        <Typography variant="h5" style={{ fontSize: "2.9em", fontWeight: 600 }}>
          <Trans>
            How do we <u>automate</u> your screening calls?
          </Trans>
        </Typography>
      </Box>
      <CreateStep />
      <Box mt={4} mb={4}>
        <InviteStep />
      </Box>
      <ReviewStep />
    </>
  );
};

const ProductBenefit = ({ icon, heading, content }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <>
      <SecondaryHeading color="primary" gutterBottom>
        <Box display="flex" alignItems="center">
          {icon}&nbsp;&nbsp;{heading}
        </Box>
      </SecondaryHeading>
      <TertiaryHeading>
        <Box pr={isSmall ? 0 : 10}>
          {content}
        </Box>
      </TertiaryHeading>
    </>
  );
};

const BenefitsArea = () => {
  const { t } = useTranslation();
  return (
    <Box p={3}>
      {/* https://v4.mui.com/components/grid/#negative-margin */}
      <Grid container spacing={6}>
        <Grid item xs={12} md={6}>
          <ProductBenefit
            icon={<SpeedIcon />}
            heading={t("Move faster")}
            content={t("Interview more people in 2 days than you would have in 2 weeks")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProductBenefit
            icon={<TrendingUpIcon />}
            heading={t("Super efficient")}
            content={t("Meet hundreds of candidates after recording yourself only once")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProductBenefit
            icon={<EventBusyIcon />}
            heading={t("No scheduling")}
            content={t("Eliminate the back-and-forth and coordination across time zones")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProductBenefit
            icon={<DoneIcon />}
            heading={t("Easy setup")}
            content={t("You'll be up-and-running in just a matter of minutes")}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const TrustedByArea = () => {
  const { t } = useTranslation();
  return (
    <Grid container spacing={6} alignItems="center" justifyContent="center">
      <Grid item xs={12} md={2}>
        <Box textAlign="center" justifyContent="center">
          <Typography variant="subtitle2">
            {t("Trusted by great teams:")}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={4} md={2}>
        <img src={ChickFilALogo} alt={t('Chick-fil-a logo')} width="100%" />
      </Grid>
      <Grid item xs={4} md={2}>
        <img src={TaipeiAmericanSchoolLogo} alt={t('Taipei American School logo')} width="100%" />
      </Grid>
      <Grid item xs={4} md={2}>
        <img src={StriveHealthLogo} alt={t('Strive Health logo')} width="100%" />
      </Grid>
      <Grid item xs={4} md={2}>
        <img src={OptimizedPaymentsLogo} alt={t('Optimized Payments logo')} width="100%" />
      </Grid>
      <Grid item xs={4} md={2}>
        <img src={MetroLagoonsLogo} alt={t('MetroLagoons logo')} width="100%" />
      </Grid>
    </Grid>
  );
};

const HeroArea = () => {
  const videoRef = useRef();
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isExactlySmall = useMediaQuery(theme.breakpoints.only('sm'));
  const isAtLeastMedium = useMediaQuery(theme.breakpoints.up('md'));
  const { trackClickToRegister } = useGoogleTagManager();

  const handleClickRegister = () => {
    trackClickToRegister(
      process.env.REACT_APP_CANVASS_REGISTER_HREF,
    );
  };

  useEffect(() => {
    // Shouldn't be necessary but just for good measure...
    if (videoRef.current) {
      videoRef.current.play();
    }
  }, [videoRef]);

  return (
    <Grid container>
      <Grid item xs={12} md={6}>
        <Box display="flex" flexDirection="column" justifyContent="center" minHeight="100%">
          <Box textAlign={isExactlySmall ? 'center' : 'left'}>
            <HeroHeading gutterBottom>
              <Trans>
                Interview candidates<br />
                <mark>10x faster</mark>
              </Trans>
            </HeroHeading>
            <Box fontSize="1.5em" mr={4} mt={4} mb={2}>
              {t('Pre-record your interview questions and invite candidates to respond in their own time using the most straightforward one-way video interviewing platform.')}
            </Box>
          </Box>
          <Box mt={2} display="flex" flexDirection={isSmall ? 'column' : 'row'}>
            <Box mr={isSmall ? 0 : 1.5} mb={isSmall ? 1.5 : 0}>
              <Button fullWidth={isSmall} variant="contained" size="large" color="primary" onClick={handleClickRegister}>
                {t("Start for free")}
              </Button>
            </Box>
            <Box>
              <Button fullWidth={isSmall} variant="contained" size="large" href={process.env.REACT_APP_CANVASS_SCHEDULE_DEMO_HREF} target="_blank">
                {t("Schedule demo")}
              </Button>
            </Box>
          </Box>
          {
            isAtLeastMedium &&
            <Box mt={1} fontStyle="italic">
              ↳ {t('No credit card required!')}
            </Box>
          }
        </Box>
      </Grid>
      <Grid item sm={12} md={6}>
        <Box display="flex" flexDirection="row-reverse" mt={isSmall ? 6 : 2}>
          <video ref={videoRef}
            height="100%"
            width="100%"
            autoPlay loop muted playsInline
            style={{ borderRadius: '8px' }}
            poster={HeroPoster}
          >
            <source src={HeroVideo} type="video/mp4" />
          </video>
        </Box>
      </Grid>
    </Grid>
  );
};

const LandingPage = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <>
      <Box mt={!isSmall ? 4 : 0} mb={10}>
        <HeroArea />
      </Box>
      <Divider />
      <Box mt={5} mb={5}>
        <TrustedByArea />
      </Box>
      <Divider />
      <Box mt={5} mb={7}>
        <BenefitsArea />
      </Box>
      <Divider />
      <Box mt={10}>
        <HowItWorksArea />
      </Box>
      <Divider />
      <Box mt={8} mb={8}>
        <ReviewsArea />
      </Box>
      <TryItNowArea />
    </>
  );
};

export default LandingPage;
